import React from 'react';
import { Helmet } from 'react-helmet';
import { SEASON_YEAR, SEASON_DATES } from '../constants/campConfig';
import { ACTIVITY_BY_MONTH, MONTH_INDEX_BY_KEY } from '../constants/activityData';

// Use SEASON_YEAR from siteConfig for consistency across the site
const SCHEDULE_YEAR = SEASON_YEAR;

// Extracts the numeric day from ordinal labels like "2nd", "11th", etc.
const parseDayOfMonth = (date) => {
  const match = date.match(/^(\d+)/);
  return match ? Number.parseInt(match[1], 10) : Number.NaN;
};

// Derives weekday text from the configured schedule year, month key, and day-of-month.
const formatWeekdayLabel = (monthKey, date, year = SCHEDULE_YEAR) => {
  const monthIndex = MONTH_INDEX_BY_KEY[monthKey];
  const dayOfMonth = parseDayOfMonth(date);

  if (monthIndex === undefined || Number.isNaN(dayOfMonth)) {
    return '';
  }

  return new Date(year, monthIndex, dayOfMonth).toLocaleDateString('en-US', { weekday: 'short' });
};

// Formats ordinal dates like "12th" as 12 with a superscript "th" suffix.
const formatDateLabel = (date) => {
  const match = date.match(/^(\d+)(st|nd|rd|th)$/i);
  if (!match) {
    return date;
  }

  return (
    <>
      {match[1]}<sup>{match[2]}</sup>
    </>
  );
};

// Returns true when an activity item has a non-empty time value.
const hasTimeValue = (item) => (
  typeof item.time === 'string'
    ? item.time.trim().length > 0
    : Boolean(item.time)
);

// Builds per-row metadata so no-time rows can share the last timed row's time cell within a date.
const buildDayRowsWithMergedTimeCells = (day) => {
  const rows = [];
  let lastTimedRowIndex = -1;

  (day.items || []).forEach((item, itemIndex) => {
    const hasTime = hasTimeValue(item);

    if (hasTime) {
      rows.push({
        item,
        itemIndex,
        showTimeCell: true,
        timeCellRowSpan: 1
      });
      lastTimedRowIndex = rows.length - 1;
      return;
    }

    if (lastTimedRowIndex >= 0) {
      rows.push({
        item,
        itemIndex,
        showTimeCell: false,
        timeCellRowSpan: 1
      });
      rows[lastTimedRowIndex].timeCellRowSpan += 1;
      return;
    }

    // No timed item exists yet in this date, so keep an empty time cell.
    rows.push({
      item,
      itemIndex,
      showTimeCell: true,
      timeCellRowSpan: 1
    });
  });

  return rows;
};

// Renders one schedule row using precomputed visibility and rowSpan rules for the time column.
const renderScheduleItemRow = (
  monthKey,
  day,
  item,
  itemIndex,
  rowKey,
  isSectionSeparatorRow = false,
  showTimeCell = true,
  timeCellRowSpan = 1
) => {
  const rowClassName = isSectionSeparatorRow ? 'separator' : undefined;
  const weekdayLabel = formatWeekdayLabel(monthKey, day.date);

  return (
    <tr key={rowKey} className={rowClassName}>
      {itemIndex === 0 && (
        <>
          <td rowSpan={day.items.length}>
            <div className="has-text-weight-semibold">{formatDateLabel(day.date)}</div>
            <div className="is-hidden-tablet">{weekdayLabel}</div>
          </td>
          <td rowSpan={day.items.length} className="is-hidden-mobile">{weekdayLabel}</td>
        </>
      )}
      {showTimeCell && <td rowSpan={timeCellRowSpan} className="has-text-right">{item.time || ''}</td>}
      <td>{item.details}</td>
    </tr>
  );
};

// Only render month headers that contain at least one schedule item.
const hasMonthScheduleRows = (month) => (
  month.sections.some((section) => (
    (section.days || []).some((day) => (day.items || []).length > 0)
  ))
);

const ActivityPage = () => {
  return (
    <div id="activity-page">
      <Helmet>
        <title>Activities &amp; Schedule | Red Rock Ponds RV Resort</title>
        <meta name="description" content={`View the full ${SEASON_YEAR} activities and events schedule at Red Rock Ponds — food trucks, live music, themed weekends, bingo, yoga, and more.`} />
      </Helmet>
      <section id="schedule" className="section">
        <div className="container">
          <h1 className="title">Activities</h1>
          <h2 className="subtitle">Activities for the <strong>{SCHEDULE_YEAR}</strong> season. More details coming soon, minor details might change without notice.</h2>
          <table className="table is-striped is-narrow is-hoverable is-fullwidth">
            <thead>
            <tr>
              <th>Date</th>
              <th className="is-hidden-mobile">Day</th>
              <th width="20%" className="has-text-right">Time</th>
              <th>Activity</th>
            </tr>
            </thead>
            <tbody>
            {ACTIVITY_BY_MONTH.filter(hasMonthScheduleRows).map((month) => (
              <React.Fragment key={month.key}>
                <tr>
                  <td colSpan="4" className="has-background-success"><strong className="has-text-white">{month.month}</strong></td>
                </tr>
                {month.sections.map((section, sectionIndex) => (
                  <React.Fragment key={`${month.key}-section-${sectionIndex}`}>
                    {section.title && (
                      <tr className="separator">
                        <td colSpan="4"><strong>{section.title}</strong></td>
                      </tr>
                    )}
                    {(() => {
                      const days = section.days || [];
                      const firstDayIndexWithItems = days.findIndex((day) => (day.items || []).length > 0);

                      return days.flatMap((day, dayIndex) => (
                        buildDayRowsWithMergedTimeCells(day).map((row) => renderScheduleItemRow(
                          month.key,
                          day,
                          row.item,
                          row.itemIndex,
                          `${month.key}-section-${sectionIndex}-day-${dayIndex}-item-${row.itemIndex}`,
                          dayIndex === firstDayIndexWithItems && row.itemIndex === 0,
                          row.showTimeCell,
                          row.timeCellRowSpan
                        ))
                      ));
                    })()}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            </tbody>
            <tfoot>
            <tr>
              <th colSpan="4">Season concludes end of day {SEASON_DATES.closingDate}.</th>
            </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ActivityPage;
